export type LyricLine = {
  time: number;
  text: string;
};

import {
  parseLqe,
  parseLyl,
  parseLys,
  parseQrc,
  parseSPL,
  parseYrc,
  type LyricLine as AmllLyricLine,
} from '@applemusic-like-lyrics/lyric';

const decodeEntities = (value: string) => value
  .replace(/&amp;/gi, '&')
  .replace(/&lt;/gi, '<')
  .replace(/&gt;/gi, '>')
  .replace(/&quot;/gi, '"')
  .replace(/&#39;|&apos;/gi, "'")
  .replace(/&#(\d+);/g, (_, code: string) => String.fromCodePoint(Number(code)))
  .replace(/&#x([\da-f]+);/gi, (_, code: string) => String.fromCodePoint(Number.parseInt(code, 16)));

const cleanText = (value: string) => decodeEntities(value)
  .replace(/<br\s*\/?\s*>/gi, '\n')
  .replace(/<[^>]+>/g, '')
  .replace(/\s*\n\s*/g, '\n')
  .trim();

const parseLrcTimestamp = (value: string) => {
  const match = value.trim().match(/^(?:(\d{1,3}):)?(\d{1,3}):(\d{2})(?:[.:](\d{1,3}))?$/);
  if (!match) return undefined;
  const hours = Number(match[1] || 0);
  const minutes = Number(match[2]);
  const seconds = Number(match[3]);
  const fraction = match[4] ? Number(`0.${match[4]}`) : 0;
  // LRC also uses cumulative minutes for long recordings ([60:01.00]).
  // Minutes are bounded only when an explicit hours component is present.
  if ((match[1] !== undefined && minutes > 59) || seconds > 59) return undefined;
  return hours * 3600 + minutes * 60 + seconds + fraction;
};

/** Parse enhanced LRC timestamps. Metadata (including [offset]) is ignored here;
 * user controlled offset is applied by the player so it can be changed live. */
export const parseLrc = (text: string): LyricLine[] => {
  const lines: LyricLine[] = [];
  for (const rawLine of text.split(/\r?\n/)) {
    const timestamps = [...rawLine.matchAll(/\[([^\]]+)\]/g)]
      .map((match) => parseLrcTimestamp(match[1]))
      .filter((time): time is number => time !== undefined);
    if (!timestamps.length) continue;
    const body = cleanText(rawLine.replace(/\[[^\]]+\]/g, ''));
    if (!body) continue;
    for (const time of timestamps) lines.push({ time, text: body });
  }
  return dedupeLines(lines);
};

const parseSrtTimestamp = (value: string) => {
  const match = value.trim().match(/^(?:(\d{1,3}):)?(\d{1,2}):(\d{2})[,.](\d{1,3})$/);
  if (!match) return undefined;
  const hours = Number(match[1] || 0);
  const minutes = Number(match[2]);
  const seconds = Number(match[3]);
  const milliseconds = Number(match[4].padEnd(3, '0'));
  if (minutes > 59 || seconds > 59) return undefined;
  return hours * 3600 + minutes * 60 + seconds + milliseconds / 1000;
};

export const parseSrt = (text: string): LyricLine[] => {
  const lines: LyricLine[] = [];
  for (const block of text.replace(/^\uFEFF/, '').split(/\r?\n\s*\r?\n/)) {
    const cue = block.match(/((?:(?:\d{1,3}:)?\d{1,2}:\d{2}[,.]\d{1,3}))\s*-->\s*((?:(?:\d{1,3}:)?\d{1,2}:\d{2}[,.]\d{1,3}))/);
    if (!cue) continue;
    const time = parseSrtTimestamp(cue[1]);
    if (time === undefined) continue;
    const cueStart = cue.index ?? 0;
    const body = block.slice(cueStart + cue[0].length);
    const cleaned = cleanText(body);
    if (cleaned) lines.push({ time, text: cleaned });
  }
  return dedupeLines(lines);
};

const parseTtmlTime = (value: string) => {
  const normalized = value.trim();
  const clock = normalized.match(/^(?:(\d+):)?(\d{1,2}):(\d{2})(?:\.(\d{1,3}))?$/);
  if (clock) return Number(clock[1] || 0) * 3600 + Number(clock[2]) * 60 + Number(clock[3]) + Number(`0.${clock[4] || '0'}`);
  const offset = normalized.match(/^(\d+(?:\.\d+)?)(ms|h|m|s)$/i);
  if (!offset) return undefined;
  const amount = Number(offset[1]);
  return offset[2].toLowerCase() === 'h' ? amount * 3600 : offset[2].toLowerCase() === 'm' ? amount * 60 : offset[2].toLowerCase() === 'ms' ? amount / 1000 : amount;
};

export const parseTtml = (text: string): LyricLine[] => {
  const lines: LyricLine[] = [];
  const paragraph = /<p\b([^>]*)>([\s\S]*?)<\/p>/gi;
  for (const match of text.replace(/^\uFEFF/, '').matchAll(paragraph)) {
    const begin = match[1].match(/\bbegin\s*=\s*["']([^"']+)["']/i)?.[1];
    const time = begin === undefined ? undefined : parseTtmlTime(begin);
    if (time === undefined) continue;
    const body = cleanText(match[2]);
    if (body) lines.push({ time, text: body });
  }
  return dedupeLines(lines);
};

/**
 * 将 AMLL 官方支持的逐词歌词格式降级为本项目的行级索引。
 * 播放时仍由 `parseAmllLyrics` 保留完整逐词结构；这里仅提供索引、同名匹配和无 AMLL 页面时的可读回退。
 */
const parseOfficialFormat = (extension: string | undefined, text: string): AmllLyricLine[] => {
  switch (extension) {
    case 'spl': return parseSPL(text);
    case 'yrc': return parseYrc(text);
    case 'qrc': return parseQrc(text);
    case 'lys': return parseLys(text);
    case 'lyl': return parseLyl(text);
    case 'lqe': return parseLqe(text);
    default: return [];
  }
};

const amllLinesToLegacy = (lines: AmllLyricLine[]): LyricLine[] => lines
  .map((line) => ({
    time: Math.max(0, line.startTime) / 1000,
    text: [line.words.map((word) => word.word).join(''), line.translatedLyric, line.romanLyric]
      .map((value) => value.trim())
      .filter(Boolean)
      .join(' '),
  }))
  .filter((line) => line.text);

const dedupeLines = (lines: LyricLine[]) => {
  const seen = new Set<string>();
  return lines
    .filter((line) => Number.isFinite(line.time) && line.time >= 0 && line.text)
    .sort((a, b) => a.time - b.time)
    .filter((line) => {
      const key = `${line.time.toFixed(3)}\u0000${line.text}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

export const parseTimedLyrics = (fileName: string, text: string): LyricLine[] => {
  const extension = fileName.toLowerCase().split('.').pop();
  if (extension === 'lrc') return parseLrc(text);
  if (extension === 'srt') return parseSrt(text);
  if (extension === 'ttml') return parseTtml(text);
  if (['spl', 'yrc', 'qrc', 'lys', 'lyl', 'lqe'].includes(extension || '')) {
    try {
      return dedupeLines(amllLinesToLegacy(parseOfficialFormat(extension, text)));
    } catch {
      return [];
    }
  }
  return [];
};

/** Shift a lyric line for display. Offset is in milliseconds; positive means later. */
export const applyLyricOffset = (lines: LyricLine[], offsetMs: number) => {
  const offset = Number.isFinite(offsetMs) ? offsetMs / 1000 : 0;
  return lines.map((line) => ({ ...line, time: Math.max(0, line.time + offset) }));
};

/** Binary-search the last line whose timestamp is not after current time. */
export const activeLyricIndex = (lines: LyricLine[], currentTime: number) => {
  let low = 0;
  let high = lines.length - 1;
  let result = -1;
  while (low <= high) {
    const middle = (low + high) >> 1;
    if (lines[middle].time <= currentTime) {
      result = middle;
      low = middle + 1;
    } else {
      high = middle - 1;
    }
  }
  return result;
};
